// /src/pages/MatchedBrokers.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import {
  FaMapMarkerAlt, FaFilter, FaArrowLeft,
  FaUserTie, FaStar, FaPlusCircle, FaShieldAlt, FaCube, FaUserCheck,
  FaTimes
} from "react-icons/fa";

import app from "../firebase";
import {
  getFirestore, collection, getDocs, query, limit as fbLimit, doc, getDoc
} from "firebase/firestore";

const db = (() => { try { return getFirestore(app); } catch { return null; } })();

const brand = { bg: "bg-[#f5f8fc]", text: "text-[#0f2437]" };
const PAGE_LIMIT = 500;

/* ===== Scheduling helpers ===== */
const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
const SMART_LEAD_MIN = 30;

function startOfTodayISO() { const d=new Date(); d.setHours(0,0,0,0); return d.toISOString().slice(0,10); }
function addDaysISO(iso, days){ const d=new Date(iso+"T00:00:00"); d.setDate(d.getDate()+days); return d.toISOString().slice(0,10); }
function makeTimeSlots({start="09:00",end="17:00",stepMin=30}){ const [sh,sm]=start.split(":").map(Number); const [eh,em]=end.split(":").map(Number); const out=[]; for(let h=sh,m=sm; h<eh||(h===eh&&m<=em);){ out.push(`${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`); m+=stepMin; if(m>=60){h+=Math.floor(m/60); m%=60;} } return out; }
function nowInTimeZone(tz){ const parts=new Intl.DateTimeFormat("en-CA",{timeZone:tz,year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit",hour12:false}).formatToParts(new Date()); const get=(t)=>parts.find(p=>p.type===t)?.value; const dateISO=`${get("year")}-${get("month")}-${get("day")}`; const minutes=parseInt(get("hour")||"0",10)*60+parseInt(get("minute")||"0",10); return {dateISO,minutes}; }
function slotToMinutes(hhmm){ const [h,m]=hhmm.split(":").map(Number); return h*60+(m||0); }
function toISOWithLocal(dateStr,timeStr){ try{ const [y,m,d]=dateStr.split("-").map(Number); const [hh,mm]=timeStr.split(":").map(Number); return new Date(y,m-1,d,hh,mm||0,0,0).toISOString(); }catch{return "";} }
function formatTimeLabel(hhmm){ const [h,m]=hhmm.split(":").map(Number); const ampm=h>=12?"PM":"AM"; const h12=h%12===0?12:h%12; return `${h12}:${String(m).padStart(2,"0")} ${ampm}`; }

/* ===== Page ===== */
export default function MatchedBrokers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const planId = params.get("plan") || location.state?.plan || null;

  // Prefer nav state questionnaire; fallback to localStorage; else synthesize a minimal one
  const questionnaireRaw = useMemo(() => {
    try {
      if (location?.state?.questionnaire) return location.state.questionnaire;
      const cached = localStorage.getItem("questionnaireResult");
      if (cached) return JSON.parse(cached);
    } catch {}
    const zip = (typeof window !== "undefined" && localStorage.getItem("userZipCode")) || null;
    return {
      answers: { partAB: null, medicaid: null, partBHelp: null, extraHelp: null, none: false },
      outcome: {
        qualifiesMA: planId === "advantage",
        dsnp: false,
        partBHelp: false,
        lis: false,
        noneOfThese: false,
      },
      plan: planId,
      zip,
      ts: Date.now(),
      version: "synthetic_v1",
    };
  }, [location?.state, planId]);

  // Normalize questionnaire into a clean, broker-friendly shape (no JSON tails)
  const questionnaire = useMemo(() => {
    const q = questionnaireRaw || {};
    const answers = q.answers || {};
    const outcome = q.outcome || {};
    return {
      plan: q.plan || planId || null,
      zip: q.zip || (typeof window !== "undefined" && localStorage.getItem("userZipCode")) || null,
      partAB: asBool(answers.partAB),
      medicaid: asBool(answers.medicaid),
      partBHelp: asBool(answers.partBHelp),
      extraHelp: asBool(answers.extraHelp),
      flags: {
        qualifiesMA: !!outcome.qualifiesMA,
        dsnp: !!outcome.dsnp,
        lis: !!outcome.lis,
      },
      // keep the raw in case backend wants it for logging/debugging
      _raw: q,
    };
  }, [questionnaireRaw, planId]);

  const [userZip, setUserZip] = useState(() => (typeof window!=="undefined" && (localStorage.getItem("userZipCode") || questionnaire?.zip)) || "");
  const [userLat, setUserLat] = useState(() => parseFloat(localStorage.getItem("userLat")) || null);
  const [userLng, setUserLng] = useState(() => parseFloat(localStorage.getItem("userLng")) || null);

  const [radius, setRadius] = useState(25);
  const [sortBy, setSortBy] = useState("distance");
  const [filters, setFilters] = useState({ dsnp: questionnaire?.flags?.dsnp || false, ma: true, partd: false, medigap: false, life: false });
  const [manualZip, setManualZip] = useState("");
  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState("");

  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // single-choice
  const [activeId, setActiveId] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const activeBroker = useMemo(() => brokers.find(b => b.id === activeId) || null, [activeId, brokers]);

  // intake
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    date: startOfTodayISO(), time: "", tz: userTZ,
    note: "I'd like to talk about my Medicare options.",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true); setError("");
      try {
        if (!db) throw new Error("Firestore not initialized");
        const snap = await getDocs(query(collection(db, "brokers"), fbLimit(PAGE_LIMIT)));
        const items = snap.docs.map((d) => {
          const data = d.data() || {};
          const specialties =
            data.specialties ||
            [data.ma && "ma", data.partd && "partd", data.medigap && "medigap", data.life && "life", data.dsnp && "dsnp"].filter(Boolean);
          return {
            id: d.id,
            ...data,
            specialties,
            about: data.about || data.bio || "",
            photoUrl: data.photoUrl || data.profilePic || data.profileURL || data.profileUrl,
            lat: parseFloat(data.lat ?? data.latitude),
            lng: parseFloat(data.lng ?? data.longitude),
          };
        });
        if (mounted) setBrokers(items);
      } catch (e) {
        console.warn("Brokers load error:", e);
        if (mounted) {
          setError("Live broker list unavailable. Showing sample layout.");
          setBrokers(sampleBrokers(userZip));
        }
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [userZip]);

  const enriched = useMemo(() =>
    brokers.map((b) => ({ ...b, __distance: computeApproxDistance({ lat: userLat, lng: userLng, zip: userZip }, b) })),
  [brokers, userLat, userLng, userZip]);

  const effectiveFilters = useMemo(() => ({
    ma: planId === "advantage" ? true : filters.ma,
    partd: planId === "partd" ? true : filters.partd,
    medigap: planId === "supplements" ? true : filters.medigap,
    life: filters.life, dsnp: filters.dsnp,
  }), [planId, filters]);

  const filtered = useMemo(() => {
    let list = enriched.slice();
    const active = Object.entries(effectiveFilters).filter(([, v]) => v).map(([k]) => k);
    list = list.filter((b) => {
      const tagSet = new Set([...(b.specialties || []), ...["ma","partd","medigap","life","dsnp"].filter((k) => b[k])]);
      if (active.length === 0) return true;
      if (tagSet.size === 0) return true;
      return active.some((k) => tagSet.has(k));
    });
    if (Number.isFinite(radius)) list = list.filter((b) => b.__distance == null || b.__distance <= radius);
    list.sort((a,b) => {
      if (sortBy==="rating") return (b.rating||0)-(a.rating||0);
      if (sortBy==="experience") return (b.yearsExperience||0)-(a.yearsExperience||0);
      const da=a.__distance??9999, db=b.__distance??9999; return da-db;
    });
    return list;
  }, [enriched, effectiveFilters, radius, sortBy]);

  function selectBroker(id){ setActiveId(id); setShowProfile(true); setSubmitted(false); setFormError(""); }

  function validateForm(){
    if (!form.name.trim()) return "Please enter your name.";
    if (!/^[\w\-.+]+@([\w-]+\.)+[A-Za-z]{2,}$/.test(form.email.trim())) return "Please enter a valid email.";
    if (!/^[0-9()+\-.\s]{7,}$/.test(form.phone.trim())) return "Please enter a valid phone number.";
    if (!form.date) return "Please choose a date.";
    if (!form.time) return "Please choose a time.";
    return "";
  }

  // Build a concise, human-friendly summary string to help the backend craft better SMS
  function buildQuestionnaireSummary() {
    const lines = [];
    if (questionnaire.zip) lines.push(`ZIP: ${questionnaire.zip}`);
    if (questionnaire.plan) lines.push(`Plan intent: ${questionnaire.plan}`);
    lines.push(`Part A & B: ${yn(questionnaire.partAB)}`);
    lines.push(`Medicaid: ${yn(questionnaire.medicaid)}${questionnaire.flags?.dsnp ? " (DSNP)" : ""}`);
    lines.push(`Part B help: ${yn(questionnaire.partBHelp)}`);
    lines.push(`Extra Help (LIS): ${yn(questionnaire.extraHelp)}`);
    return lines.join("\n");
  }

  async function submitIntake(){
    const err = validateForm();
    if (err) { setFormError(err); return; }

    const { dateISO: todayInTZ, minutes: nowMin } = nowInTimeZone(form.tz);
    if (form.date === todayInTZ && slotToMinutes(form.time) < nowMin + SMART_LEAD_MIN) {
      setFormError("That time has passed. Please choose a later slot.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      const iso = toISOWithLocal(form.date, form.time);

      // send a clean object + a preformatted summary string (backend can choose either)
      const cleanQuestionnaire = {
        plan: questionnaire.plan,
        zip: questionnaire.zip,
        partAB: questionnaire.partAB,
        medicaid: questionnaire.medicaid,
        partBHelp: questionnaire.partBHelp,
        extraHelp: questionnaire.extraHelp,
        flags: questionnaire.flags,
        summary: buildQuestionnaireSummary(),
        _raw: questionnaire._raw ?? null,
      };

      const payload = {
        broker: { id: activeBroker?.id, name: activeBroker?.name || "", phone: activeBroker?.phone || "", zip: activeBroker?.zip || "" },
        intake: { ...form, iso },
        userZip,
        plan: planId,
        questionnaire: cleanQuestionnaire,
      };

      const endpoint =
        process.env.REACT_APP_CALLBACK_FN_URL ||
        "https://us-central1-yourchoiceassociates-49454.cloudfunctions.net/requestCallback";

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify(payload),
      });

      const raw = await resp.text();
      let data; try { data = raw ? JSON.parse(raw) : null; } catch {}
      if (!resp.ok) {
        const msg = (data && (data.error || data.message)) || raw || `HTTP ${resp.status}`;
        throw new Error(msg);
      }

      setSubmitted(true);
    } catch (e) {
      console.error("requestCallback error:", e);
      setFormError("We couldn't book the SMS. Please try again.");
    } finally { setSubmitting(false); }
  }

  async function updateZip() {
    const z = manualZip.trim();
    if (!isValidZipFormat(z)) { setZipError("Enter a valid 5-digit U.S. ZIP code."); return; }
    setZipLoading(true); setZipError("");
    try {
      const meta = await lookupZipMeta(z);
      if (!meta || !Number.isFinite(meta.lat) || !Number.isFinite(meta.lng)) throw new Error("ZIP not found");
      localStorage.setItem("userZipCode", z);
      localStorage.setItem("userLat", String(meta.lat));
      localStorage.setItem("userLng", String(meta.lng));
      try { localStorage.setItem(`zipMeta:${z}`, JSON.stringify(meta)); } catch {}
      setUserZip(z); setUserLat(meta.lat); setUserLng(meta.lng); setManualZip("");
    } catch { setZipError("We couldn't verify that ZIP. Please try another."); }
    finally { setZipLoading(false); }
  }

  const timeSlots = useMemo(() => makeTimeSlots({ start: "09:00", end: "17:00", stepMin: 30 }), []);
  const availableSlots = useMemo(() => {
    const base = timeSlots;
    const { dateISO: todayInTZ, minutes: nowMin } = nowInTimeZone(form.tz);
    if (form.date === todayInTZ) {
      const cutoff = nowMin + SMART_LEAD_MIN;
      return base.filter((t) => slotToMinutes(t) >= cutoff);
    }
    return base;
  }, [timeSlots, form.date, form.tz]);

  useEffect(() => {
    const { dateISO: todayInTZ } = nowInTimeZone(form.tz);
    setForm((prev) => {
      let next = { ...prev };
      if (next.date === todayInTZ && availableSlots.length === 0) {
        next.date = addDaysISO(todayInTZ, 1);
        next.time = "";
      }
      if (next.time && !availableSlots.includes(next.time)) {
        next.time = availableSlots[0] || "";
      }
      return (next.date !== prev.date || next.time !== prev.time) ? next : prev;
    });
  }, [form.tz, form.date, availableSlots.length]);

  return (
    <div className={`${brand.bg} ${brand.text} min-h-screen flex flex-col`}>
      <Helmet><title>Matched Brokers Near You | Your Choice Associates</title></Helmet>
      <Navbar />

      <main className="flex-grow py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Matched Brokers</h1>
              <p className="mt-1 text-gray-600">Based on your answers{userZip ? ` and ZIP ${userZip}` : ""}. Choose a broker nearby — it doesn't have to be your exact ZIP.</p>
              {questionnaire?.flags?.dsnp && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-emerald-800 text-sm">
                  <span className="inline-flex items-center gap-1"><FaPlusCircle /> DSNP eligible</span>
                </div>
              )}
            </div>

            {/* ZIP & Controls */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 rounded-full border bg-white px-3 py-2 shadow-sm">
                <FaMapMarkerAlt className="text-[#e31c3d]" />
                <input placeholder={userZip || "ZIP"} className="outline-none text-sm w-24"
                  value={manualZip}
                  onChange={(e)=>{ setZipError(""); setManualZip(e.target.value.replace(/[^0-9]/g,"").slice(0,5)); }}
                />
                <button onClick={updateZip} disabled={zipLoading || !isValidZipFormat(manualZip)} className="text-xs font-semibold text-[#0B5EFD] disabled:opacity-50">
                  {zipLoading ? "Updating…" : "Update"}
                </button>
              </div>

              <div className="flex items-center gap-2 rounded-full border bg-white px-3 py-2 shadow-sm">
                <FaFilter />
                <span className="text-sm">Within</span>
                <select className="text-sm outline-none" value={radius} onChange={(e)=>setRadius(parseInt(e.target.value,10))}>
                  {[5,10,25,50,100].map(r => <option key={r} value={r}>{r} mi</option>)}
                </select>
              </div>

              <div className="flex items-center gap-2 rounded-full border bg-white px-3 py-2 shadow-sm">
                <span className="text-sm">Sort</span>
                <select className="text-sm outline-none" value={sortBy} onChange={(e)=>setSortBy(e.target.value)}>
                  <option value="distance">Distance</option>
                  <option value="rating">Rating</option>
                  <option value="experience">Experience</option>
                </select>
              </div>
            </div>
          </div>

          {/* Filter chips */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <FilterChip active={effectiveFilters.ma} onClick={()=>setFilters(f=>({...f,ma:!f.ma}))} icon={<FaPlusCircle/>} label="Medicare Advantage"/>
            <FilterChip active={effectiveFilters.partd} onClick={()=>setFilters(f=>({...f,partd:!f.partd}))} icon={<FaCube/>} label="Part D"/>
            <FilterChip active={effectiveFilters.medigap} onClick={()=>setFilters(f=>({...f,medigap:!f.medigap}))} icon={<FaShieldAlt/>} label="Medigap"/>
            <FilterChip active={effectiveFilters.life} onClick={()=>setFilters(f=>({...f,life:!f.life}))} icon={<FaUserCheck/>} label="Life"/>
            <FilterChip active={effectiveFilters.dsnp} onClick={()=>setFilters(f=>({...f,dsnp:!f.dsnp}))} icon={<FaPlusCircle/>} label="DSNP"/>
          </div>

          {/* List + Profile Panel */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* list */}
            <div className={`${showProfile ? "lg:col-span-2" : "lg:col-span-3"}`}>
              <div className="rounded-2xl bg-white border border-gray-200 p-4 md:p-6">
                {loading ? (
                  <ListSkeleton />
                ) : (
                  <>
                    {error && <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 text-sm">{error}</div>}
                    {filtered.length === 0 ? (
                      <div className="text-center text-gray-600 py-8">No brokers matched your filters. Try expanding your radius or clearing filters.</div>
                    ) : (
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filtered.map(b => (
                          <li key={b.id}
                              className={`group relative rounded-2xl border p-4 shadow-sm transition hover:shadow-md cursor-pointer ${activeId===b.id?"border-[#002b5c] ring-1 ring-[#002b5c]":"border-gray-200"}`}
                              onClick={()=>selectBroker(b.id)}
                              role="button"
                              aria-pressed={activeId===b.id}
                          >
                            <div className="flex items-start gap-4">
                              <div className="h-16 w-16 rounded-xl bg-gray-100 overflow-hidden shrink-0">
                                {b.photoUrl ? <img src={b.photoUrl} alt={b.name} className="h-full w-full object-cover"/> :
                                  <div className="h-full w-full flex items-center justify-center text-gray-400"><FaUserTie/></div>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-lg truncate">{b.name || "Licensed Broker"}</h3>
                                <div className="mt-1 text-sm text-gray-600 flex flex-wrap items-center gap-2">
                                  <FaMapMarkerAlt className="text-[#e31c3d]" />
                                  <span>{b.city || ""}{b.state ? `, ${b.state}` : ""} {b.zip ? `• ${b.zip}` : ""}</span>
                                  {b.__distance != null && <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{fmtMiles(b.__distance)}</span>}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2 text-xs">{renderTags(b)}</div>
                                {(b.rating || b.yearsExperience) && (
                                  <div className="mt-2 text-xs text-gray-600 flex items-center gap-3">
                                    {b.rating && <span className="inline-flex items-center gap-1"><FaStar className="text-amber-400" /> {b.rating.toFixed ? b.rating.toFixed(1) : b.rating}/5</span>}
                                    {b.yearsExperience && <span>{b.yearsExperience} yrs experience</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* profile panel */}
            {showProfile && activeBroker && (
              <aside className="lg:col-span-1">
                <div className="rounded-2xl bg-white border border-gray-200 p-4 md:p-6 sticky top-6">
                  <div className="flex items-start justify-between">
                    <h2 className="text-lg font-semibold">Broker Profile</h2>
                    <button className="h-8 w-8 inline-flex items-center justify-center rounded-full border" onClick={()=>setShowProfile(false)}>
                      <FaTimes />
                    </button>
                  </div>

                  <div className="mt-4 flex items-start gap-4">
                    <div className="h-16 w-16 rounded-xl bg-gray-100 overflow-hidden shrink-0">
                      {activeBroker.photoUrl ? <img src={activeBroker.photoUrl} alt={activeBroker.name} className="h-full w-full object-cover"/> :
                        <div className="h-full w-full flex items-center justify-center text-gray-400"><FaUserTie/></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-xl">{activeBroker.name}</div>
                      <div className="mt-1 text-sm text-gray-600 flex flex-wrap items-center gap-2">
                        <FaMapMarkerAlt className="text-[#e31c3d]" />
                        <span>{activeBroker.city || ""}{activeBroker.state ? `, ${activeBroker.state}` : ""} {activeBroker.zip ? `• ${activeBroker.zip}` : ""}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">{renderTags(activeBroker)}</div>
                    </div>
                  </div>

                  {activeBroker.about && <p className="mt-4 text-sm text-gray-700 leading-6">{activeBroker.about}</p>}

                  {/* request callback */}
                  <div className="mt-6">
                    <div className="text-sm font-semibold mb-2">Request a callback</div>
                    <div className="grid gap-3">
                      <input className="rounded-lg border p-2" placeholder="Your name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/>
                      <input className="rounded-lg border p-2" placeholder="Your email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}/>
                      <input className="rounded-lg border p-2" placeholder="Your phone" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})}/>

                      <div className="grid grid-cols-1 gap-4">
                        <SmartCalendar value={form.date} tz={form.tz} onChange={(d)=>setForm(f=>({...f, date:d, time:""}))}/>
                        <TimeGridPicker dateISO={form.date} tz={form.tz} slots={timeSlots} availableSlots={availableSlots} value={form.time} onChange={(t)=>setForm(f=>({...f, time:t}))}/>
                        <p className="text-[11px] text-gray-500">Showing times in {form.tz.replace(/_/g," ")}. Past times today are hidden.</p>
                      </div>

                      <div className="inline-flex items-center gap-2 rounded-full bg-gray-50 border px-3 py-1 text-xs text-gray-700 w-fit">
                        <span>Timezone:</span>
                        <select className="bg-transparent outline-none" value={form.tz} onChange={(e)=>setForm({...form, tz:e.target.value})}>
                          {["America/New_York","America/Chicago","America/Denver","America/Los_Angeles"].map(tz => <option key={tz} value={tz}>{tz.replace(/_/g," ")}</option>)}
                        </select>
                      </div>

                      <textarea className="rounded-lg border p-2 min-h-[90px]" value={form.note} onChange={(e)=>setForm({...form, note:e.target.value})}/>

                      {formError && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900 text-sm">{formError}</div>}
                      {submitted && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900 text-sm">Thanks! We’ve notified the broker with your answers and your preferred time. They’ll reach out to confirm.</div>}

                      <div className="flex justify-end">
                        <button onClick={submitIntake} disabled={submitting} className="rounded-full bg-[#002b5c] px-6 py-2.5 text-white font-semibold disabled:opacity-40">
                          {submitting ? "Submitting…" : "Request callback"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </aside>
            )}
          </div>

          <div className="mt-6">
            <button onClick={()=>navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[#0B5EFD]"><FaArrowLeft/> Back</button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ---- Calendar + Time grid components ---- */
function SmartCalendar({ value, tz, onChange }) {
  const todayTZ = nowInTimeZone(tz).dateISO;
  const [cursor, setCursor] = useState(value || todayTZ);
  const [y, m] = cursor.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const monthName = first.toLocaleString("en-US", { month: "long", timeZone: tz });
  const year = first.getFullYear();
  const startWeekday = (new Date(year, first.getMonth(), 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, first.getMonth() + 1, 0).getDate();

  const cells = []; for (let i=0;i<startWeekday;i++) cells.push(null);
  for (let d=1; d<=daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  function moveMonth(delta){ const next = new Date(year, first.getMonth()+delta, 1); setCursor(next.toISOString().slice(0,10)); }
  function isDisabled(day){ if(!day) return true; const iso=`${year}-${String(first.getMonth()+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`; return iso < todayTZ; }
  const selectedISO = value;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold">{monthName} {year}</div>
        <div className="flex gap-2">
          <button className="h-8 w-8 rounded-full border hover:bg-gray-50" onClick={()=>moveMonth(-1)} aria-label="Previous month">↑</button>
          <button className="h-8 w-8 rounded-full border hover:bg-gray-50" onClick={()=>moveMonth(+1)} aria-label="Next month">↓</button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] text-gray-500 mb-1">{["Mo","Tu","We","Th","Fr","Sa","Su"].map(d => <div key={d} className="py-1">{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx} className="h-9" />;
          const iso = `${year}-${String(first.getMonth()+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
          const disabled = isDisabled(day);
          const selected = iso === selectedISO;
          const base = "h-9 rounded-lg flex items-center justify-center text-sm";
          const styles = disabled ? "text-gray-300 border border-transparent"
            : selected ? "bg-[#002b5c] text-white"
            : "hover:bg-gray-100 border border-transparent";
          return <button key={idx} className={`${base} ${styles}`} disabled={disabled} onClick={()=>onChange(iso)}>{day}</button>;
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-gray-600">
        <button type="button" className="underline" onClick={()=>onChange(todayTZ)}>Today</button>
        <div className="inline-flex items-center gap-2 rounded-full bg-gray-50 border px-2 py-0.5">
          <span>Timezone:</span><span className="font-medium">{tz.replace(/_/g," ")}</span>
        </div>
      </div>
    </div>
  );
}

function TimeGridPicker({ dateISO, tz, slots, availableSlots, value, onChange }) {
  const isToday = dateISO === nowInTimeZone(tz).dateISO;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="mb-2 font-semibold">Select a time</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {slots.map((t) => {
          const isDisabled = isToday ? !availableSlots.includes(t) : false;
          const isSelected = value === t;
          const base = "rounded-full border text-sm px-3 py-2 text-center";
          const styles = isDisabled ? "text-gray-300 border-gray-200 cursor-not-allowed"
            : isSelected ? "bg-[#002b5c] text-white border-[#002b5c]"
            : "border-gray-300 hover:border-[#002b5c]";
          return <button key={t} type="button" disabled={isDisabled} className={`${base} ${styles}`} onClick={()=>onChange(t)}>{formatTimeLabel(t)}</button>;
        })}
      </div>
      {isToday && <p className="mt-2 text-[11px] text-gray-500">Past times today are hidden/disabled.</p>}
    </div>
  );
}

/* ---- bits ---- */
function yn(b){ return b === true ? "Yes" : b === false ? "No" : "—"; }
function asBool(v){ return v === true ? true : v === false ? false : null; }
function Preset({ children, onClick }) { return <button type="button" onClick={onClick} className="rounded-full border px-3 py-1 text-xs hover:border-[#002b5c]">{children}</button>; }
function renderTags(b){ const tags=[]; const has=(k)=>!!(b?.specialties?.includes?.(k)||b?.[k]===true); if(has("ma"))tags.push(tag("Medicare Advantage")); if(has("partd"))tags.push(tag("Part D")); if(has("medigap"))tags.push(tag("Medigap")); if(has("life"))tags.push(tag("Life")); if(has("dsnp"))tags.push(tag("DSNP")); return tags; }
function tag(text){ return <span key={text} className="inline-flex items-center rounded-full bg-[#eef2f7] px-2.5 py-1 text-gray-700">{text}</span>; }
function ListSkeleton(){ return (<ul className="grid grid-cols-1 md:grid-cols-2 gap-4">{Array.from({length:6}).map((_,i)=>(<li key={i} className="rounded-2xl border border-gray-200 p-4 shadow-sm animate-pulse"><div className="flex items-start gap-4"><div className="h-16 w-16 rounded-xl bg-gray-200"/><div className="flex-1"><div className="h-4 w-1/2 bg-gray-200 rounded"/><div className="mt-2 h-3 w-2/3 bg-gray-200 rounded"/><div className="mt-2 h-3 w-1/3 bg-gray-200 rounded"/></div></div><div className="mt-3 h-3 w-24 bg-gray-200 rounded"/></li>))}</ul>); }
function fmtMiles(m){ return m==null?"nearby":`${Math.round(m)} mi`; }
function computeApproxDistance(user, broker){ if(isFinite(user?.lat)&&isFinite(user?.lng)&&isFinite(broker?.lat)&&isFinite(broker?.lng)){ return haversine(user.lat,user.lng,broker.lat,broker.lng);} const uz=(user?.zip||"").toString(); const bz=(broker?.zip||"").toString(); if(uz&&bz){ if(uz.slice(0,3)===bz.slice(0,3)) return 5; if(uz.slice(0,2)===bz.slice(0,2)) return 25; } return 100; }
function haversine(lat1,lon1,lat2,lon2){ const R=3958.8, toRad=v=>(v*Math.PI)/180; const dLat=toRad(lat2-lat1), dLon=toRad(lon2-lon1); const a=Math.sin(dLat/2)**2+Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLon/2)**2; return 2*R*Math.atan2(Math.sqrt(a),Math.sqrt(1-a)); }
function isValidZipFormat(zip){ return /^\d{5}$/.test(zip) && zip >= "00501" && zip <= "99950"; }
async function lookupZipMeta(zip){
  try{ const cached=localStorage.getItem(`zipMeta:${zip}`); if(cached) return JSON.parse(cached);}catch{}
  if(db){ for(const col of ["zips","zip_codes","zipcodes"]){ try{ const snap=await getDoc(doc(db,col,zip)); if(snap?.exists?.()){ const d=snap.data(); const meta={ city:d.city||d.place||d.name, state:d.state||d.state_abbr, lat:parseFloat(d.lat??d.latitude), lng:parseFloat(d.lng??d.longitude) }; if(Number.isFinite(meta.lat)&&Number.isFinite(meta.lng)){ try{ localStorage.setItem(`zipMeta:${zip}`, JSON.stringify(meta)); }catch{} return meta; } } }catch{} } }
  try{ const res=await fetch(`https://api.zippopotam.us/us/${zip}`); if(!res.ok) throw new Error("bad"); const data=await res.json(); const p=data?.places?.[0]; const meta={ city:p?.["place name"]||p?.place, state:p?.["state abbreviation"]||p?.state, lat:parseFloat(p?.latitude), lng:parseFloat(p?.longitude) }; if(Number.isFinite(meta.lat)&&Number.isFinite(meta.lng)){ try{ localStorage.setItem(`zipMeta:${zip}`, JSON.stringify(meta)); }catch{} return meta; } }catch{} return null;
}
function sampleBrokers(zip){ return [{ id:"s1", name:"Alex Gomez", city:"New York", state:"NY", zip:zip||"10025", ma:true, partd:true, dsnp:true, rating:4.8, yearsExperience:9, phone:"(555) 555-1212" }, { id:"s2", name:"Brooke Chen", city:"New York", state:"NY", zip:"10024", ma:true, medigap:true, rating:4.6, yearsExperience:7, phone:"(555) 987-2222" }]; }
function FilterChip({active,onClick,icon,label}){ return (<button type="button" onClick={onClick} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition ${active?"bg-gray-900 text-white border-gray-900":"border-gray-300 hover:border-gray-500 bg-white"}`}><span className="opacity-90">{icon}</span>{label}</button>); }
